import React, { useState, useRef, useEffect } from "react"
import Box from "@mui/material/Box"
import Webcam from "react-webcam"
import Spinner from "../common/Spinner"
import FlashOverlay from "../common/FlashOverlay"
import CountdownView from "../common/CountdownView"
import IconButton from "../buttons/IconButton"
import { ReactComponent as CameraIcon } from "../../icon/camera.svg"
import PermissionModal from "../modals/PermissionModal"
import { CSSTransition } from "react-transition-group"
import { useTranslation } from "react-i18next"
import FaceGuide from "../../image/face-guide.png"
import shutterSound from "../../sound/camera-shutter.mp3"
import beepSound from "../../sound/beep.mp3"
import { cropImage } from "../../utils/ImageUtil"
import { useCameraPermission } from "../../hooks/useCameraPermission"
import MediaContainer from "../common/MediaContainer"

interface CameraViewProps {
  theme: any
  startCapture: boolean
  selectedCamera: MediaDeviceInfo | null
  showFaceGuide?: boolean
  showCameraButton: boolean
  onStartCapture: () => void
  postDelay: number
  handleDevices: (devices: MediaDeviceInfo[]) => void
  finishCountdown: () => void
  handleImage: (image: string, source: string) => void
  handleError: (error: string) => void
}

function CameraView({
  theme,
  startCapture,
  selectedCamera,
  showFaceGuide,
  showCameraButton,
  onStartCapture,
  postDelay,
  handleDevices,
  finishCountdown,
  handleImage,
  handleError,
}: CameraViewProps) {
  const { t } = useTranslation()
  const [timerValue, setTimerValue] = useState(-1)
  const [showModal, setShowModal] = useState(false)
  const [mediaReceived, setMediaReceived] = useState(false)
  const [sequenceStarted, setSequenceStarted] = useState(false)
  const webcam = useRef<any>(null)
  const canvas = useRef<HTMLCanvasElement | null>(null)

  const { isGranted, isLoading } = useCameraPermission()

  const receiveFirstMedia = () => {
    setMediaReceived(true)
    setShowModal(false)

    navigator.mediaDevices.enumerateDevices().then((mediaDevices) => {
      let videoDevices = mediaDevices.filter(({ kind }) => kind === "videoinput")
      handleDevices(videoDevices)
    })
  }

  // Show permission modal after delay, but only if permission isn't already granted
  useEffect(() => {
    if (isLoading || isGranted || mediaReceived) return

    const timeoutId = setTimeout(() => {
      if (!mediaReceived) {
        setShowModal(true)
      }
    }, 800)

    return () => clearTimeout(timeoutId)
  }, [isLoading, isGranted, mediaReceived])

  const startSequence = () => {
    setTimerValue(3)
    tick(3)
  }

  const tick = (currentValue: number) => {
    if (currentValue >= 1) {
      setTimeout(() => {
        let newValue = currentValue - 1
        setTimerValue(newValue)
        if (newValue > 0) {
          tick(newValue)
        }
        if (newValue === 0) {
          finishCountdown()
          setTimeout(() => {
            captureImage()
          }, 200)
        }
      }, 1000)
    }
  }

  const captureImage = () => {
    if (!webcam || !webcam.current) {
      return
    }

    webcam.current.video?.pause()

    let imageData = webcam.current.getScreenshot()
    let image = new Image()
    image.src = imageData
    image.onload = function () {
      let cropPixels = {
        x: 0,
        y: (image.naturalHeight - image.naturalWidth) / 2,
        width: image.naturalWidth,
        height: image.naturalWidth,
      }

      if (image.naturalWidth > image.naturalHeight) {
        cropPixels = {
          x: (image.naturalWidth - image.naturalHeight) / 2,
          y: 0,
          width: image.naturalHeight,
          height: image.naturalHeight,
        }
      }

      if (!canvas.current) {
        throw new Error("Canvas missing")
      }

      imageData = cropImage(image, canvas.current, cropPixels, mirrored)

      setTimeout(() => {
        handleImage(imageData, "camera")
      }, postDelay)
    }
  }

  // Start countdown
  if (startCapture && timerValue === -1 && !sequenceStarted) {
    setSequenceStarted(true)
    setTimeout(startSequence, 800)
  }

  let videoConstraints: any = {
    aspectRatio: 1,
    width: 1920,
    height: 1080,
  }

  let mirrored = true
  if (selectedCamera) {
    videoConstraints.deviceId = selectedCamera.deviceId
    if (selectedCamera.label.toLowerCase().includes("back")) {
      mirrored = false
    }
  }

  // Play beep sound on each countdown tick
  useEffect(() => {
    if (timerValue > 0) {
      const audio = new Audio(beepSound)
      audio.volume = 0.5
      audio.play()
    }
  }, [timerValue])

  // Play shutter sound when capture completes
  useEffect(() => {
    if (timerValue === 0) {
      const audio = new Audio(shutterSound)
      audio.play()
    }
  }, [timerValue])

  const imgFill = { display: "block", position: "absolute", inset: 0, width: "100%" } as const
  const overlaySx = {
    position: "relative",
    inset: 0,
    backgroundSize: "100% 100%",
    zIndex: 3,
    pointerEvents: "none",
  } as const

  return (
    <>
      <canvas
        className="hiddenCanvas"
        ref={canvas}
      ></canvas>

      <FlashOverlay show={timerValue === 0} />

      <CSSTransition
        in
        appear
        timeout={500}
        exit={false}
        classNames="camera"
        unmountOnExit
      >
        <MediaContainer
          sx={{
            userSelect: "none",
            WebkitTouchCallout: "none",
            mx: "auto",
          }}
        >
          <CountdownView value={timerValue} />

          <CSSTransition
            in={theme.firstOverlayUrl && mediaReceived}
            appear
            timeout={500}
            classNames="fade"
            unmountOnExit
          >
            <Box sx={overlaySx}>
              <Box
                component="img"
                crossOrigin="anonymous"
                src={theme.firstOverlayUrl}
                alt="Overlay"
                sx={imgFill}
              />
            </Box>
          </CSSTransition>

          <CSSTransition
            in={showFaceGuide}
            appear
            timeout={500}
            classNames="fade"
            unmountOnExit
          >
            <Box sx={overlaySx}>
              <Box
                component="img"
                crossOrigin="anonymous"
                src={FaceGuide}
                alt="Guide"
                sx={imgFill}
              />
            </Box>
          </CSSTransition>


          <Box sx={{ position: "absolute", inset: 0, zIndex: 1 }}>
            <Webcam
              ref={webcam}
              audio={false}
              mirrored={mirrored}
              imageSmoothing
              forceScreenshotSourceSize
              videoConstraints={videoConstraints}
              onUserMedia={receiveFirstMedia}
              onUserMediaError={(exception) => {
                handleError(typeof exception === "string" ? exception : exception.message)
              }}
            />
          </Box>

          {timerValue === -1 && (
            <Spinner
              color={theme.textStyle.color}
              centerVertical
            />
          )}
        </MediaContainer>
      </CSSTransition>

      <CSSTransition
        in={showCameraButton}
        appear
        timeout={300}
        classNames="iconAnimate"
        unmountOnExit
      >
        <Box sx={{ textAlign: "center", mt: "-54px", position: "relative", zIndex: 100 }}>
          <IconButton
            theme={theme}
            onPress={onStartCapture}
          >
            <CameraIcon style={{ ...theme.svgStyle, width: 48, height: 48 }} />
          </IconButton>
        </Box>
      </CSSTransition>

      <PermissionModal show={showModal} />
    </>
  )
}

export default CameraView
